import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { AGENCY_TZ, ApiError, api, formatDate, formatDateTime, formatPhone } from "../api";
import { useAuth } from "../auth";
import DateRangePicker from "../components/DateRangePicker";
import useCallHistory from "../components/useCallHistory";
import { datesLabel, resolveRange, todayIn } from "../date-range";
import { LsaCallActions } from "../components/LsaCalls";
import LsaSummary, { type LsaSummaryData } from "../components/LsaSummary";
import FilterPill from "../components/FilterPill";
import { DISPOSITIONS, dispositionLabel } from "../dispositions";
import { callerLabel, callTimeLabel, holdLabel, leadIdOf, phoneLabel, territoryOptions, type Territory } from "../lsa-call-format";
import CallbackQueue from "../components/CallbackQueue";
import { useClientSelection } from "../client-context";
import { useApi } from "../hooks";
import { EmptyState, PageHeader, MetricCard } from "../components/Ui";
import Dialog from "../components/Dialog";
import ConnectionNotice from "../components/ConnectionNotice";
import {
  DataTable,
  Details,
  NoClient,
  RequestView,
  Tabs,
  label,
  type RecordData,
} from "../components/Workflow";

/** Provider keys ("google_ads", "gbp") read as names: "Google Ads", "GBP". */
const sourceName = (value: unknown) => (value == null || value === "" ? value : label(String(value)));
/**
 * The CTM number a call rang: its own label in CTM ("Website call button"), else
 * the number itself. Every row used to say "CTM (Google Ads number)", which told
 * nothing a row apart.
 */
const numberOf = (r: RecordData) => {
  const name = String(r.tracking_label ?? "").trim();
  if (name) return name;
  return r.tracking_number ? formatPhone(String(r.tracking_number)) : null;
};
/** "Aug 26": a day in the agency's own year needs no year. */
const shortDay = (day: string) => formatDate(day).replace(/, \d{4}$/, "");

const year = (d: Date) => new Intl.DateTimeFormat("en-US", { year: "numeric", timeZone: AGENCY_TZ }).format(d);
/** "Aug 23, 11:08 AM": when a call came in, on the agency's clock (the year only when it is not this one). */
function callTime(value: unknown) {
  const d = new Date(String(value ?? ""));
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short", day: "numeric", ...(year(d) === year(new Date()) ? {} : { year: "numeric" }),
    hour: "numeric", minute: "2-digit", timeZone: AGENCY_TZ,
  }).format(d);
}

// Call trackers send names in capitals ("R CHRISTIAN") and, when the caller ID
// has no name, the caller's city and state padded out ("SACRAMENTO   CA").
const STATES = new Set("AL AK AZ AR CA CO CT DE FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY DC".split(" "));
const titleCase = (s: string) => s.toLowerCase().replace(/(^|[\s\-'])([a-z])/g, (m) => m.toUpperCase());
function callerOf(r: RecordData): { name: string | null; place: string | null } {
  const raw = String(r.caller_name ?? "");
  const name = raw.replace(/\s+/g, " ").trim();
  const m = /^([A-Za-z .'-]+?) ([A-Z]{2})$/.exec(name);
  const isPlace = !!m && STATES.has(m[2]) && (/\S\s{2,}[A-Z]{2}\s*$/.test(raw) || String(r.city ?? "").toLowerCase() === m[1].toLowerCase());
  const fallback = r.city ? [titleCase(String(r.city)), r.state].filter(Boolean).join(", ") : null;
  if (!name || isPlace) return { name: null, place: isPlace && m ? `${titleCase(m[1])}, ${m[2]}` : fallback };
  return { name: name === name.toUpperCase() && /[A-Z]/.test(name) ? titleCase(name) : name, place: null };
}
function Caller({ r }: { r: RecordData }) {
  const { name, place } = callerOf(r);
  if (name) return <span>{name}</span>;
  if (r.caller_phone || r.caller_phone_last4) return <span className="caller-ending">{callerLabel(r)}</span>;
  return <span>Unknown caller{place && <span className="call-place"> {place}</span>}</span>;
}
/** CTM's outcome labels in plain words: "Non-Booked" reads "Not booked". */
const outcomeLabel = (v: unknown) => (v == null || v === "" ? v : /^non[\s-]?booked$/i.test(String(v)) ? "Not booked" : String(v));
const RECOVERY: Record<string, string> = { HIGH: "High", MEDIUM: "Medium", LOW: "Low", NONE: "None" };
const recoveryLabel = (v: unknown) => (v == null || v === "" ? null : RECOVERY[String(v).toUpperCase()] || label(String(v).toLowerCase()));
// One score format on every call surface: "6/10" (the lists said "3/10", the detail "3 out of 10").
const ctmScore = (call: RecordData) => call.nepq_score != null
  ? `${call.nepq_score}/10`
  : call.reviewed_at ? 'Not applicable' : call.has_transcript ? 'Awaiting review' : 'No transcript';

type Feed = { status: string; last_good_at: string | null; last_call_at: string | null };
type Stats = {
  paid_call_performance: RecordData | null;
  feed?: Feed;
  /** The days the CTM figures cover; last_data when the feed stopped and they end on its last good day. */
  ctm_period?: { from: string; to: string; last_data: boolean };
  google_ads_calls?: number | null;
  google_calls_since?: string | null;
  period_start?: string;
  scope_warning?: string;
};
type CallHistory = { calls: RecordData[]; total: number; page: number; page_size: number };
type TranscriptLine = { speaker: string | null; text: string };

/** CTM's transcript of one call, read on request: most calls are never opened. */
function Transcript({ clientId, callId, rowId }: { clientId: number; callId: string; rowId: number }) {
  const [open, setOpen] = useState(false);
  const transcript = useApi<{ lines: TranscriptLine[] }>(open ? `/api/ctm/${clientId}/calls/${encodeURIComponent(callId)}/transcript?row_id=${rowId}` : null);
  if (!open) return <button type="button" className="button secondary call-transcript-toggle" onClick={() => setOpen(true)}>Read transcript</button>;
  return (
    <section className="call-transcript" aria-label="Call transcript">
      <div className="call-transcript-head">
        <h3>Transcript</h3>
        <button type="button" className="text-button" onClick={() => setOpen(false)}>Hide</button>
      </div>
      <RequestView request={transcript}>
        <div className="call-transcript-body">
          {!transcript.data?.lines?.length && <p>No transcript text is available for this call.</p>}
          {(transcript.data?.lines || []).map((l, i) => (
            <p key={i}>{l.speaker && <strong>{l.speaker}</strong>}{l.text}</p>
          ))}
        </div>
      </RequestView>
    </section>
  );
}

function CtmFollowUp({ clientId, call, canEdit, onSaved }: { clientId: number; call: RecordData; canEdit: boolean; onSaved: (row: RecordData) => void }) {
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  async function save(flag: boolean) {
    setBusy(true); setError('');
    try { onSaved(await api<RecordData>(`/api/ctm/${clientId}/calls/${call.id}/follow-up`, { method: 'PUT', body: JSON.stringify({ office_follow_up: flag }) })); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  return <div className="call-review-controls"><label className="call-follow-up"><input type="checkbox" checked={!!call.office_follow_up} disabled={busy || !canEdit} onChange={e => void save(e.target.checked)} />Called back{busy ? ': saving…' : ''}</label>{error && <p role="alert" className="inline-notice danger">{error}</p>}</div>;
}

function Calls({ clientId }: { clientId: number }) {
  const { user } = useAuth();
  const canEdit = ["superadmin", "exec"].includes(user?.role || "");
  const admin = user?.role === "superadmin";
  const { view, setView, query, setQuery, page, setPage, range, setRange, allHistory, setAllHistory, territory, setTerritory, show, setShow } = useCallHistory();
  const [selected, setSelected] = useState<RecordData | null>(null);
  const [scan, setScan] = useState<{ source?: string; busy?: boolean; error?: string; notice?: string }>({});
  const [confirmScan, setConfirmScan] = useState(false);
  const scanInFlight = useRef(false);
  const [sorting, setSorting] = useState<{ busy?: boolean; notice?: string; error?: string }>({});
  // An agent picked in the close-rate table narrows the list to their calls.
  const [agent, setAgent] = useState("");
  const stats = useApi<Stats>(`/api/ctm/${clientId}/stats?days=30`);
  // This page counts through today (calls arrive live), unlike the Ads pages, which end on the
  // last synced day: the tiles name their dates so the two "Last 30 days" can be told apart.
  const today = todayIn();
  const dates = resolveRange(range, today, 'today');
  const period = allHistory ? "All history" : datesLabel(dates.from, dates.to, today);
  const historyQuery = new URLSearchParams({ page: String(page), page_size: '25', q: query,
    ...(allHistory ? {} : { from: dates.from, to: dates.to }) }).toString();
  const showQuery = (show.startsWith("d:") ? `&disposition=${encodeURIComponent(show.slice(2))}` : show ? `&show=${show}` : "")
    + (territory ? `&territory=${encodeURIComponent(territory)}` : "")
    + (agent ? `&agent=${encodeURIComponent(agent)}` : "");
  const summaryQuery = new URLSearchParams({ ...(allHistory ? {} : { from: dates.from, to: dates.to }), ...(territory ? { territory } : {}) }).toString();
  const territories = useApi<Territory[]>(view === "scoring_and_coaching" ? `/api/lsa/${clientId}/territories` : null);
  const manyTerritories = (territories.data?.length || 0) > 1;
  const lsaSummary = useApi<LsaSummaryData>(view === "scoring_and_coaching" ? `/api/lsa/${clientId}/summary?${summaryQuery}` : null);
  // The call-back list, one call at a time (components/CallbackQueue.tsx).
  const [queueOpen, setQueueOpen] = useState(false);
  const alertSettings = useApi<RecordData>(view === "scoring_and_coaching" && canEdit ? `/api/notifications/${clientId}/settings` : null);
  const calls = useApi<CallHistory>(view === "ad_calls" ? `/api/ctm/${clientId}/calls?${historyQuery}` : null);
  const diagnostics = useApi<CallHistory>(view === "scoring_and_coaching" ? `/api/lsa/${clientId}/calls?${historyQuery}${showQuery}` : null);
  const history = view === "ad_calls" ? calls : diagnostics;
  const totalCalls = history.data?.total || 0;
  useEffect(() => { setSelected(null); setConfirmScan(false); }, [view]);
  // Most Junk King locations have LSA calls and no CTM account: opened without a tab
  // chosen, the page starts on LSA calls instead of an empty CTM tab (user test 10-28).
  // A CTM account that stopped updating (refused key, failing, stale) counts too: its
  // tab holds only old calls, while LSA calls are current.
  const pickedTab = useRef(new URLSearchParams(window.location.search).has("call_view"));
  const ctmNotCurrent = ["not_connected", "needs_key", "failing", "stale"].includes(stats.data?.feed?.status || "");
  useEffect(() => {
    if (pickedTab.current || !ctmNotCurrent) return;
    pickedTab.current = true;
    let cancelled = false;
    api<Territory[]>(`/api/lsa/${clientId}/territories`).then((list) => { if (!cancelled && Array.isArray(list) && list.length) setView("scoring_and_coaching"); }).catch(() => {});
    return () => { cancelled = true; };
  }, [ctmNotCurrent, clientId]);
  useEffect(() => {
    if (history.data && !history.loading && page > Math.max(1, Math.ceil(totalCalls / 25))) setPage(Math.max(1, Math.ceil(totalCalls / 25)));
  }, [history.data, history.loading, page, totalCalls]);
  const pipeline = useApi<{ stages: RecordData[] }>(
    view === "ghl_activity" ? `/api/ghl/${clientId}/pipeline` : null,
  );
  const analysis = useApi<RecordData>(
    view === "ghl_activity" ? `/api/ghl/${clientId}/analysis` : null,
  );
  const detail = useApi<RecordData>(
    selected && view === "scoring_and_coaching"
      ? `/api/lsa/${clientId}/calls/${selected.id}`
      : null,
  );
  const paid = stats.data?.paid_call_performance;
  const feed = stats.data?.feed;
  const ctmWindow = stats.data?.ctm_period;
  // CTM linked but not delivering: the tiles count the 30 days up to its last good
  // day and say so. With no good day at all its counts are unknown, not 0.
  const ctmStopped = !!feed && ["needs_key", "failing", "stale"].includes(feed.status);
  const ctmMissing = feed?.status === "not_connected";
  const lastData = ctmStopped && !!ctmWindow?.last_data;
  const ctmUnknown = (ctmStopped && !lastData) || ctmMissing;
  const ctmDetail = lastData && ctmWindow
    ? `${shortDay(ctmWindow.from)} to ${shortDay(ctmWindow.to)} (last CTM data)`
    : ctmStopped
      ? `Not updating since ${feed?.last_good_at ? shortDay(feed.last_good_at) : "it was set up"}`
      : ctmMissing ? "No CTM account linked" : null;
  const ctmCalls = Number(paid?.paid_calls ?? 0);
  const googleCalls = stats.data?.google_ads_calls;
  // A source this client never had: the tab shows one empty state with the way to link it,
  // not a toolbar, tiles and offers that all say "not linked". LSA calls come through the
  // Google Ads account; past LSA calls (a territory with calls) keep the list.
  const lsaMissing = !!stats.data && googleCalls == null && !!territories.data && !territories.data.length;
  const sourceMissing = view === "ad_calls" ? ctmMissing : view === "scoring_and_coaching" && lsaMissing;
  const linkSource = admin ? <Link className="button secondary" to={`/client-settings?client_id=${clientId}`}>Open Connections</Link> : undefined;
  const googleSince = stats.data?.google_calls_since && stats.data.period_start && stats.data.google_calls_since > stats.data.period_start ? stats.data.google_calls_since : null;

  const rows =
    (view === "ad_calls" ? calls.data?.calls : diagnostics.data?.calls) || [];
  // The number column earns its place only when calls came in on more than one number.
  const showNumber = view === "ad_calls" && new Set(rows.map((r) => numberOf(r) ?? "")).size > 1;
  // The row opens its call in a dialog in place: the date is the row's button,
  // stretched over the whole row, so the table needs no button per row.
  const dateKey = view === "ad_calls" ? "called_at" : "call_date";
  const tableRows = rows
    .map((r) => {
      const lsaTime = view === "ad_calls" ? null : callTimeLabel(r);
      const when = view === "ad_calls" ? callTime(r.called_at) : `${formatDate(r.call_date)}${lsaTime ? `, ${lsaTime}` : ""}`;
      const who = callerOf(r).name || (r.caller_phone || r.caller_phone_last4 ? callerLabel(r) : "an unknown caller");
      return {
        ...r,
        ...(view === "ad_calls" ? { number: numberOf(r) } : { source: sourceName(r.source) }),
        outcome: outcomeLabel(r.outcome),
        // LSA: the lead ID sits under the caller instead of taking a column of its own (ten columns wrapped every cell).
        caller_name: view === "ad_calls" || !leadIdOf(r)
          ? <Caller r={r} />
          : <span className="call-caller"><Caller r={r} /><small className="call-lead-id">Lead {leadIdOf(r)}</small></span>,
        call_score: view === 'ad_calls' ? ctmScore(r) : r.nepq_score == null ? 'No score' : `${r.nepq_score}/10`, recovery_chance: recoveryLabel(r.recovery_probability),
        ...(view === "ad_calls" ? {} : { coaching_note: r.coaching_note ? <span className="clamp-2" title={String(r.coaching_note)}>{r.coaching_note}</span> : null, disposition: dispositionLabel(r.disposition), office_follow_up: r.office_follow_up ? "Yes" : <span className="call-not-back">—</span>, lead_id: leadIdOf(r), lsa_rating: r.needs_lsa_rating ? "To rate in LSA" : r.lsa_rated_at ? "Rated" : null }),
        [dateKey]: (
          <button type="button" className="call-row-open" aria-label={`View call from ${who}, ${when}`} onClick={() => setSelected(r)}>
            {view === "ad_calls" ? when : <><span className="call-when-date">{formatDate(r.call_date)}</span>{lsaTime && <small className="call-when-time">{lsaTime}</small>}</>}
          </button>
        ),
      };
    });

  async function scanCalls() {
    const isCtm = view === "ad_calls";
    if (scanInFlight.current) return;
    scanInFlight.current = true;
    setConfirmScan(false);
    setScan({ source: view, busy: true });
    try {
      const r = await api<{ status: string; processed: number; total_leads: number; remaining?: number; errors: string[] }>(isCtm ? `/api/ctm/${clientId}/score` : `/api/lsa/${clientId}/scan`, {
        method: "POST",
        body: JSON.stringify(isCtm ? {} : { day_range: 30, scan_source: "adhoc", include_pending: true }),
      });
      const problems = [...new Set((r.errors || []).map((e) => e.replace(/^[A-Z_]+:\s*/, "")))];
      const scored = r.processed === 1 ? "1 call reviewed" : `${r.processed} calls reviewed`;
      const left = r.remaining ? ` ${r.remaining === 1 ? "1 call is" : `${r.remaining} calls are`} left: press ${isCtm ? "Score CTM calls" : "Scan LSA calls"} again to finish.` : "";
      setScan({
        source: view,
        notice: problems.length ? `Scan finished with problems: ${scored}. ${problems.join(" ")}${left}`
          : !isCtm && !r.total_leads ? "Scan finished. No LSA calls were returned for the last 30 days and no saved recordings are waiting for a transcript."
          : isCtm && !r.processed && !r.remaining ? "No saved CTM transcripts are waiting for review."
          : `Scan finished: ${scored}.${left}`,
      });
      diagnostics.reload(); calls.reload(); territories.reload();
    } catch (e) {
      // The scan outlived the request: what it scored so far is saved, and a second scan skips it.
      const providerError = e instanceof ApiError && typeof e.data?.code === "string" && e.data.code.startsWith("LSA_");
      const slow = e instanceof ApiError && ([0, 504].includes(e.status) || (e.status === 502 && !providerError));
      setScan({ source: view, error: slow ? `The scan is taking longer than the page can wait. Calls scored so far are saved; press ${isCtm ? "Score CTM calls" : "Scan LSA calls"} again to finish.` : e instanceof Error ? e.message : "The scan could not run." });
    } finally {
      scanInFlight.current = false;
    }
  }

  async function sortCalls() {
    setSorting({ busy: true });
    try {
      const r = await api<{ sorted: number; failed: number; remaining: number; agents_read?: number; agents_remaining?: number }>(`/api/lsa/${clientId}/dispositions`, { method: "POST", body: "{}" });
      // The same press also reads who answered each older call (close rate by agent).
      const left = r.remaining + (r.agents_remaining || 0);
      setSorting({ notice: `${r.sorted === 1 ? "1 call" : `${r.sorted} calls`} sorted${r.agents_read ? `, ${r.agents_read} read for their agent` : ""}.${r.failed ? ` ${r.failed} could not be read and will be tried again.` : ""}${left ? ` More left: press Sort past calls again.` : ""}` });
      lsaSummary.reload(); diagnostics.reload();
    } catch (e) {
      setSorting({ error: e instanceof ApiError && [0, 504].includes(e.status) ? "Sorting is taking longer than the page can wait. Calls sorted so far are saved; press Sort past calls again." : e instanceof Error ? e.message : "Calls could not be sorted." });
    }
  }
  const callbackOn = alertSettings.data?.callback_email_enabled === true;
  const callbackTo = Array.isArray(alertSettings.data?.callback_emails) ? (alertSettings.data?.callback_emails as string[]) : [];
  const counts = new Map((lsaSummary.data?.dispositions || []).map((d) => [d.disposition, d.calls]));
  const showOptions = [
    { value: "", name: "All calls", count: lsaSummary.data?.calls },
    { value: "callback", name: "Call-back chances", count: lsaSummary.data?.callback_chances },
    { value: "not_called_back", name: "Call-back chances not called back", count: lsaSummary.data?.callbacks_open },
    { value: "called_back", name: "Called back", count: lsaSummary.data?.called_back },
    ...(canEdit ? [{ value: "rate", name: "Rate in LSA", count: lsaSummary.data?.needs_rating }] : []),
    ...Object.entries(DISPOSITIONS).map(([key, name]) => ({ value: `d:${key}`, name, count: counts.get(key) || 0 })),
  ];

  const callRecord = (r: RecordData): RecordData => ({
    called_at: <>{formatDateTime(r.called_at)}</>,
    caller: <Caller r={r} />,
    caller_number: r.caller_number,
    tracking_number: r.tracking_number ? <>{formatPhone(r.tracking_number)}{r.tracking_label ? ` (${r.tracking_label})` : ""}</> : null,
    status: r.call_status,
    talk_time: r.talk_time,
    outcome: outcomeLabel(r.outcome),
    source: r.source ? `${sourceName(r.source)}, tracked by CTM` : "Tracked by CTM",
    summary: r.summary,
    call_score: ctmScore(r),
    reviewed_at: r.reviewed_at,
    recovery_chance: recoveryLabel(r.recovery_probability),
    what_happened: r.diagnostic_text,
    coaching_note: r.coaching_note,
    buyer_signals: r.buyer_signals,
    objections: r.objections_detected,
  });
  const lsaRecord = (c: RecordData): RecordData => ({
    call_date: c.call_date,
    time: callTimeLabel(c),
    territory: c.territory,
    caller: callerLabel(c),
    phone: phoneLabel(c.caller_phone) !== callerLabel(c) ? phoneLabel(c.caller_phone) : null,
    lead_id: leadIdOf(c),
    duration: c.duration_seconds,
    agent: c.agent_name || null,
    time_on_hold: holdLabel(c),
    disposition: c.disposition ? `${dispositionLabel(c.disposition)}${c.disposition_note ? `. ${c.disposition_note}` : ""}` : null,
    lead_status: c.lead_status ? label(String(c.lead_status).toLowerCase()) : null,
    charged_by_google: typeof c.charged === "boolean" ? (c.charged ? "Yes" : "No") : null,
    call_score: c.nepq_score == null ? 'No score' : `${c.nepq_score}/10`,
    recovery_chance: recoveryLabel(c.recovery_probability),
    what_happened: c.diagnostic_text,
    coaching_note: c.coaching_note,
    buyer_signals: Array.isArray(c.buyer_signals) && c.buyer_signals.length ? c.buyer_signals : null,
    objections: Array.isArray(c.objections_detected) && c.objections_detected.length ? c.objections_detected : null,
    transcript: c.transcript,
    call_back_email: c.callback_alert_status ? `${label(String(c.callback_alert_status))}${c.callback_alert_at ? `, ${formatDateTime(c.callback_alert_at)}` : ""}` : null,
    recovery_alert: c.notification_status ? c.notification_status === 'sending' ? 'Delivery unconfirmed' : label(c.notification_status) : c.notification_sent ? 'Previously marked sent; delivery unverified' : 'Not sent',
    alert_delivery_issues: c.notification_detail?.errors?.length ? c.notification_detail.errors : null,
    alert_delivery: c.notification_detail ? `${c.notification_detail.delivered || 0} delivered, ${c.notification_detail.queued || 0} queued, ${c.notification_detail.errors?.length || 0} delivery issues` : null,
  });

  return (
    <>
      <Tabs
        tabs={["ad_calls", "scoring_and_coaching", "ghl_activity"]}
        labels={{ ad_calls: 'CTM calls', scoring_and_coaching: 'LSA calls', ghl_activity: 'GHL activity' }}
        value={view}
        onChange={(v) => {
          setView(v);
          setSelected(null);
        }}
      />
      {/* Ad calls come from CallTrackingMetrics; the GHL tab from each client's GHL sync. */}
      <ConnectionNotice clientId={clientId} feeds={view === "ghl_activity" ? ["ghl"] : view === "ad_calls" ? ["ctm"] : []} />
      {view === "ad_calls" && (
        <RequestView request={stats}>
          {/* Without CTM its three tiles each said "No CTM account linked"; the list below says it once. */}
          {!(ctmMissing && googleCalls == null) && <div className="module-metrics calls-metrics">
            {!ctmMissing && <>
              <MetricCard
                label="CTM calls"
                value={ctmUnknown ? "—" : ctmCalls.toLocaleString("en-US")}
                detail={ctmDetail || (ctmWindow ? `${shortDay(ctmWindow.from)} to ${shortDay(ctmWindow.to)}` : "Last 30 days")}
              />
              <MetricCard
                label="Answered"
                value={ctmUnknown || paid?.answer_rate == null || !ctmCalls ? "—" : `${Math.round(Number(paid.answer_rate))}%`}
                detail={ctmDetail || (ctmCalls ? "Of CTM calls" : "No CTM calls in the last 30 days")}
              />
              <MetricCard
                label="Booked"
                value={ctmUnknown ? "—" : Number(paid?.booked ?? 0).toLocaleString("en-US")}
                detail={ctmDetail || "CTM outcomes, last 30 days"}
              />
            </>}
            {/* Google counts these itself, so they are not rows in the CTM table below; the Ads report holds them. */}
            <MetricCard
              label="Google Ads calls"
              value={googleCalls == null ? "—" : googleCalls.toLocaleString("en-US")}
              to={googleCalls == null ? undefined : `/ads/${clientId}`}
              detail={googleCalls == null ? "No Google Ads account linked" : `Counted by Google${googleSince ? ` since ${formatDate(googleSince).replace(/, \d{4}$/, "")}` : ", last 30 days"}. Details in Ads report`}
            />
          </div>}
        </RequestView>
      )}
      {view === "scoring_and_coaching" && lsaSummary.data && lsaSummary.data.calls > 0 && <LsaSummary data={lsaSummary.data} period={period} agent={agent} onAgent={(name) => { setAgent((current) => current.toLowerCase() === name.toLowerCase() ? "" : name); setPage(1); }} />}
      {view === "scoring_and_coaching" && agent && <p className="lsa-agent-filter">Showing {agent}&apos;s calls. <button type="button" className="text-button" onClick={() => { setAgent(""); setPage(1); }}>Show all calls</button></p>}
      {view === "scoring_and_coaching" && canEdit && alertSettings.data && !lsaMissing && (
        <p className="lsa-callback-line">
          <strong>Call-back emails</strong>
          {callbackOn
            ? <span>On. {alertSettings.data.callback_level === "MEDIUM" ? "High and medium" : "High"} chance calls go to {callbackTo.join(", ")} within about 5 minutes.</span>
            : <span>Off. The owner can get an email with the caller&apos;s number within minutes of a call worth saving.</span>}
          <Link to={`/portal?client_id=${clientId}&view=settings&section=notifications`}>{callbackOn ? "Change" : "Turn on"}</Link>
        </p>
      )}
      {view !== "ghl_activity" ? (
        <section className="panel calls-panel">
          {/* The list's actions sit on its heading row; the toolbar under it holds the filters
              only (they shared one wrapping row, six rows tall on a phone). */}
          <div className="panel-heading calls-panel-heading">
            <div>
              <h2>{view === "ad_calls" ? "Calls tracked by CTM" : "LSA calls"}</h2>
              {view === "ad_calls" && stats.data?.scope_warning && !ctmMissing && <p>{stats.data.scope_warning}</p>}
            </div>
            {!sourceMissing && (
              <div className="calls-panel-actions">
                {view === "scoring_and_coaching" && (lsaSummary.data?.callbacks_open || 0) > 0 && (
                  <button className="button primary" onClick={() => setQueueOpen(true)}>Work the call-back list ({lsaSummary.data!.callbacks_open})</button>
                )}
                {view === "scoring_and_coaching" && canEdit && !!(lsaSummary.data?.unsorted || lsaSummary.data?.agents_unread) && (
                  <button className="button secondary" disabled={sorting.busy} onClick={() => void sortCalls()}>{sorting.busy ? "Sorting…" : "Sort past calls"}</button>
                )}
                {view === "scoring_and_coaching" && canEdit && googleCalls != null && (
                  <button className="button secondary" disabled={scan.busy} onClick={() => setConfirmScan(true)}>
                    {scan.busy && scan.source === view ? "Scanning…" : "Scan LSA calls"}
                  </button>
                )}
                {view === "ad_calls" && canEdit && <button className="button secondary" disabled={scan.busy} onClick={() => setConfirmScan(true)}>{scan.busy && scan.source === view ? "Scoring…" : "Score CTM calls"}</button>}
                <button
                  className="button secondary"
                  onClick={() => {
                    calls.reload();
                    diagnostics.reload();
                    stats.reload();
                  }}
                >
                  Refresh
                </button>
              </div>
            )}
          </div>
          {sourceMissing ? (
            view === "ad_calls"
              ? <EmptyState title="No CTM account linked" description={admin ? "Link this client's CallTrackingMetrics account to see its calls here." : "An administrator links accounts in Setup, Connections."} action={linkSource} />
              : <EmptyState title="No Google Ads account linked" description={admin ? "LSA calls show here once this client's Google Ads account is linked." : "An administrator links accounts in Setup, Connections."} action={linkSource} />
          ) : (<>
            <div className="toolbar calls-filters">
              <label className="search-control">
                <input
                  aria-label="Search calls"
                  placeholder={view === 'ad_calls' ? 'Search caller, outcome or summary' : 'Search caller, number or lead status'}
                  value={query}
                  maxLength={200}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </label>
              <DateRangePicker value={range} maxDays={36500} rollingEnd="today" label="Call dates" onChange={setRange} />
              <button className={`button secondary${allHistory ? ' active' : ''}`} aria-pressed={allHistory} onClick={() => setAllHistory(!allHistory)}>All history</button>
              {view === "scoring_and_coaching" && manyTerritories && (
                <FilterPill label="Territory" value={territory} options={territoryOptions(territories.data)} onChange={setTerritory} />
              )}
              {view === "scoring_and_coaching" && (
                <FilterPill label="Show" value={show} options={showOptions} onChange={setShow} />
              )}
            </div>
            {scan.busy && scan.source !== view && <p className="inline-notice" role="status">{scan.source === 'ad_calls' ? 'CTM call scoring' : 'The LSA scan'} is still running. Return to that tab to see its progress.</p>}
            {scan.source === view && scan.error && <p className="inline-notice danger" role="alert">{scan.error}</p>}
            {scan.source === view && scan.notice && <p className="inline-notice" role="status">{scan.notice}</p>}
            {view === "scoring_and_coaching" && sorting.notice && <p className="inline-notice" role="status">{sorting.notice}</p>}
            {view === "scoring_and_coaching" && sorting.error && <p className="inline-notice danger" role="alert">{sorting.error}</p>}
            <RequestView request={view === "ad_calls" ? calls : diagnostics}>
              <div className="calls-table">
                <DataTable
                  rows={tableRows}
                  columns={
                    view === "ad_calls"
                      ? ["called_at", "caller_name", "caller_number", "outcome", "call_score", "recovery_chance", "office_follow_up", ...(showNumber ? ["number"] : [])]
                      : ["call_date", ...(manyTerritories && !territory ? ["territory"] : []), "caller_name", "disposition", "call_score", "recovery_chance", "office_follow_up", ...(canEdit ? ["lsa_rating"] : []), "coaching_note"]
                  }
                  empty={
                    view === "ad_calls"
                      // CTM stopped: its tiles show its last 30 days of data, so say why this list is empty.
                      ? lastData && ctmWindow && !allHistory && !query
                        ? <>No CTM calls in these dates: CTM's last calls came in by {shortDay(ctmWindow.to)}. <button type="button" className="text-button" onClick={() => setAllHistory(true)}>Show all history</button></>
                        : "No CTM calls match these dates or search."
                      : googleCalls == null
                        ? <>No Google Ads account is linked to this client.{admin && <> <Link to={`/client-settings?client_id=${clientId}`}>Link Google Ads in Setup, Connections</Link>.</>}</>
                        : show ? "No LSA calls match this filter in these dates." : "No LSA calls match these dates or search."
                  }
                  action={() => <ChevronRight className="call-row-chevron" aria-hidden="true" />}
                />
              </div>
              {/* An empty list says so once (the table's own line); one page needs no pager. */}
              {totalCalls > 0 && (
                <div className="show-more" aria-label="Call history pages">
                  <span className="muted">{totalCalls > 25 ? `${(page - 1) * 25 + 1} to ${Math.min(page * 25, totalCalls)} of ${totalCalls} calls` : `${totalCalls} ${totalCalls === 1 ? "call" : "calls"}`}{allHistory ? ', all history' : ''}</span>
                  {totalCalls > 25 && <>
                    <button className="button secondary" disabled={page <= 1 || history.loading || history.refreshing} onClick={() => setPage(page - 1)}>Previous</button>
                    <button className="button secondary" disabled={page * 25 >= totalCalls || history.loading || history.refreshing} onClick={() => setPage(page + 1)}>Next</button>
                  </>}
                </div>
              )}
            </RequestView>
          </>)}
        </section>
      ) : (
        <div className="workflow-stack">
          {!pipeline.loading && !pipeline.error && !(pipeline.data?.stages || []).length ? (
            <section className="panel">
              <EmptyState
                title="No GoHighLevel pipeline for this client yet"
                description="Pipeline stages, stalled opportunities and appointments show here once GoHighLevel is connected and synced."
                action={admin ? <Link className="button" to={`/client-settings?client_id=${clientId}`}>Connect GHL</Link> : undefined}
              />
            </section>
          ) : (
            <>
              <section className="panel">
                <div className="panel-heading">
                  <div><h2>Pipeline stages</h2></div>
                </div>
                <RequestView request={pipeline}>
                  <DataTable rows={pipeline.data?.stages || []} />
                </RequestView>
              </section>
              <section className="panel workflow-panel">
                <h2>Pipeline activity and follow-up</h2>
                <RequestView request={analysis}>
                  {analysis.data && <Details record={analysis.data} />}
                </RequestView>
              </section>
            </>
          )}
        </div>
      )}
      {confirmScan && (
        <Dialog title={view === "ad_calls" ? "Score CTM calls?" : "Scan LSA calls?"} onClose={() => setConfirmScan(false)}>
          <form className="operation-form" onSubmit={(event) => { event.preventDefault(); void scanCalls(); }}>
            {view === "ad_calls" ? <p>Score saved CTM transcripts that have not been reviewed. AI scoring costs a little per call. CTM’s existing transcripts are reused. This does not send email, SMS or Slack alerts.</p> : <><p>Scan the last 30 days of Local Services Ads calls and retry saved recordings waiting for a transcript.</p>
            <p>Retry failed reviews using their saved transcripts, without transcribing again.</p>
            <p>AI transcription and scoring cost a little per call. Calls returned by Google can send recovery alerts to the configured recipients. Retrying saved recordings missing from Google’s results does not send alerts. Retrying a failed saved review does not resend alerts.</p></>}
            <div className="operation-actions">
              <button type="button" className="button secondary" onClick={() => setConfirmScan(false)}>Cancel</button>
              <button type="submit" className="button" disabled={scan.busy}>{view === "ad_calls" ? "Start scoring" : "Start scan"}</button>
            </div>
          </form>
        </Dialog>
      )}
      {queueOpen && <CallbackQueue clientId={clientId} filters={summaryQuery} onClose={() => { setQueueOpen(false); lsaSummary.reload(); diagnostics.reload(); }} />}
      {selected && (
        <Dialog title="Call details" onClose={() => setSelected(null)}>
          {view === "scoring_and_coaching" ? (
            <RequestView request={detail}>
              {detail.data && <><LsaCallActions key={selected.id} call={detail.data} clientId={clientId} canEdit={canEdit} onSaved={() => { detail.reload(); diagnostics.reload(); lsaSummary.reload(); }} /><Details record={lsaRecord(detail.data)} /></>}
            </RequestView>
          ) : (
            <>
              <CtmFollowUp key={selected.id} clientId={clientId} call={selected} canEdit={canEdit} onSaved={updated => { setSelected(current => current?.id === updated.id ? { ...current, ...updated } : current); calls.reload(); }} />
              <Details record={callRecord(selected)} />
              {selected.has_transcript && selected.call_id ? <Transcript key={String(selected.call_id)} clientId={clientId} callId={String(selected.call_id)} rowId={Number(selected.id)} /> : null}
            </>
          )}
        </Dialog>
      )}
    </>
  );
}

export default function Intelligence() {
  const { clientId, client } = useClientSelection();
  return (
    <>
      <PageHeader
        title="Calls & coaching"
        description={`Calls, call scoring and GHL pipeline for ${client?.name || "the active client"}.`}
      />
      {clientId ? <Calls key={clientId} clientId={clientId} /> : <NoClient />}
    </>
  );
}
